// @ts-strict-ignore
import { DashboardModal } from "@dashboard/components/Modal";
import { LinearProgress } from "@material-ui/core";
import { 
  Box, 
  Button, 
  Spinner,
  Text} from "@saleor/macaw-ui-next";
import React, { useCallback,useState } from "react";
import { FormattedMessage } from "react-intl";

export interface BulkProductUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, onProgress?: (progress: number) => void) => Promise<void>;
}

interface UploadState {
  status: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
  fileName?: string;
  progress?: number;
}

const BulkProductUploadModal = ({ open, onClose, onUpload }: BulkProductUploadModalProps) => {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle'
  });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setUploadState({ status: 'idle', progress: 0 });
      setDragActive(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadState({
        status: 'error',
        error: 'Please upload a CSV file'
      });

      return;
    }

    setUploadState({
      status: 'uploading',
      fileName: file.name,
      progress: 0
    });

    try {
      await onUpload(file, (progress: number) => {
        setUploadState(prev => ({
          ...prev,
          progress: Math.round(progress)
        }));
      });
      
      setUploadState({
        status: 'success',
        fileName: file.name,
        progress: 100
      });

      // Auto-close after success
      setTimeout(() => {
        onClose();
        setUploadState({ status: 'idle' });
      }, 2000);
    } catch (error) {
      setUploadState({
        status: 'error',
        error: error.message || 'Upload failed',
        fileName: file.name
      });
    }
  }, [onUpload, onClose]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleReset = useCallback(() => {
    setUploadState({ status: 'idle', progress: 0 });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const renderUploadContent = () => {
    if (uploadState.status === 'uploading') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
          <Spinner />
          <Text size={4} fontWeight="medium">
            <FormattedMessage
              id="Bk87wu"
              defaultMessage="Uploading {fileName}..."
              values={{ fileName: uploadState.fileName }}
            />
          </Text>
          <Box width="100%" style={{ maxWidth: '320px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
              <Text size={2} color="default2">
                <FormattedMessage
                  id="sIMS7i"
                  defaultMessage="Progress"
                />
              </Text>
              <Text size={2} fontWeight="medium">
                {uploadState.progress ?? 0}%
              </Text>
            </Box>
            <LinearProgress variant="determinate" value={uploadState.progress ?? 0} />
          </Box>
        </Box>
      );
    }

    if (uploadState.status === 'success') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
          <Text size={4} fontWeight="medium" color="success1">
            <FormattedMessage
              id="Z7LbU+"
              defaultMessage="Products uploaded successfully!"
            />
          </Text>
          <Text size={2}>
            {uploadState.fileName}
          </Text>
        </Box>
      );
    }

    if (uploadState.status === 'error') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
          <Text size={4} fontWeight="medium" color="critical1">
            <FormattedMessage
              id="f00p/7"
              defaultMessage="Upload failed"
            />
          </Text>
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={2} 
            padding={4}
            borderRadius={2}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '400px', 
              overflow: 'auto',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '8px'
            }}
          >
            <Text size={2} style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word', 
              color: '#7f1d1d',
              fontFamily: 'monospace',
              lineHeight: '1.4'
            }}>
              {uploadState.error}
            </Text>
          </Box>
          <Button variant="secondary" onClick={handleReset}>
            <FormattedMessage
              id="FazwRl"
              defaultMessage="Try again"
            />
          </Button>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={4}>
        <Text size={3}>
          <FormattedMessage
            id="Ti1WsZ"
            defaultMessage="Drag and drop a CSV file or click to browse."
          />
        </Text>
        
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={4}
          padding={12}
          borderRadius={3}
          borderStyle="solid"
          borderWidth={1}
          borderColor={dragActive ? "accent1" : "default1"}
          backgroundColor={dragActive ? "accent1" : "transparent"}
          style={{ 
            cursor: 'pointer',
            borderStyle: dragActive ? 'dashed' : 'dashed',
            transition: 'all 0.2s ease',
            minHeight: '200px'
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
        >
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            width={16}
            height={16}
            marginBottom={2}
          >
            <Text size={8} style={{ fontSize: '48px' }}>↑</Text>
          </Box>
          
          <Text size={5} fontWeight="medium" textAlign="center">
            <FormattedMessage
              id="cZ5c95"
              defaultMessage="Drag & drop your CSV file here"
            />
          </Text>
          
          <Text size={3} textAlign="center">
            <FormattedMessage
              id="Ntjkqd"
              defaultMessage="or"
            />
          </Text>
          
          <Button variant="secondary">
            <FormattedMessage
              id="M88YzU"
              defaultMessage="Browse Files"
            />
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
        </Box>
      </Box>
    );
  };

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="md">
        <DashboardModal.Header>
          <DashboardModal.Title>
            <FormattedMessage
              id="cVSdOK"
              defaultMessage="Upload CSV File"
            />
          </DashboardModal.Title>
        </DashboardModal.Header>

        <Box padding={6}>
          {renderUploadContent()}
        </Box>

        {uploadState.status === 'idle' && (
          <DashboardModal.Actions>
            <Button variant="secondary" onClick={onClose}>
              <FormattedMessage
                id="47FYwb"
                defaultMessage="Cancel"
              />
            </Button>
          </DashboardModal.Actions>
        )}
      </DashboardModal.Content>
    </DashboardModal>
  );
};

BulkProductUploadModal.displayName = "BulkProductUploadModal";
export default BulkProductUploadModal;